import RouteTemplate from 'ember-route-template'
import Component from '@glimmer/component'
import { service } from '@ember/service'
import { SubscriptionType } from 'teacher-ui-client/subscription-type/subscription-types'
import { LessonResourceType } from 'teacher-ui-client/contentful-client/entry-types'
import type {
  LessonDataEntry,
  LessonPreviewEntry,
  LessonResourceEntry,
} from 'teacher-ui-client/contentful-client/entry-types'
import { LessonPreviewTile } from 'teacher-ui-client/components/tiles/lesson/preview'
import { t } from 'ember-intl'
import { LessonsPage } from 'teacher-ui-client/components/page/lessons'
import { NoResultsMessage } from 'teacher-ui-client/components/no-results-message'
import LessonsSearch from 'teacher-ui-client/components/lessons/search'
import BasePageSelector from 'teacher-ui-client/components/base/page-selector'
import LessonFields from 'teacher-ui-client/components/lesson/fields'
import { concat } from '@ember/helper'
import type InteractivePreviewService from 'teacher-ui-client/services/interactive-preview'
import { WritingLegendsLessonPreview } from 'teacher-ui-client/interactive-preview/engines'
import { ThemeKeys } from 'teacher-ui-client/utils/themes'
import type WritingLegendsLessonsRoute from 'teacher-ui-client/subscription-type/lessons/writing-legends/previews/route'

interface Signature {
  Args: Args
}

interface Args {
  model: ModelFor<WritingLegendsLessonsRoute>
}

const RESOURCE_ORDER: ReadonlyArray<LessonResourceType> = [
  LessonResourceType.STUDENT_PDF,
  LessonResourceType.TEACHER_PDF,
  LessonResourceType.ES_STUDENT_PDF,
]

export class WritingLegendsPreviews extends Component<Signature> {
  @service interactivePreview!: InteractivePreviewService

  readonly subscriptionType: SubscriptionType = SubscriptionType.Writing

  get lessonPreviewViewModels() {
    if (!this.args.model.loaderInstance?.isSuccessful) return []

    const entries = this.args.model.loaderInstance.value?.lessonsData.items ?? []
    const previews = this.args.model.loaderInstance.value?.previewByLessonDataId ?? {}
    const resources = this.args.model.loaderInstance.value?.resourcesByLessonDataId ?? {}
    const { curriculum } = this.interactivePreview

    return entries.map((entry) => {
      const lessonDataId = entry.sys.id
      const activityNumber = entry.fields.activityNumber
      return lessonPreviewViewModel({
        lessonData: entry,
        lessonPreview: previews[lessonDataId],
        lessonResources: resources[lessonDataId] ?? [],
        openPreview: () =>
          this.interactivePreview.openPreview(
            new WritingLegendsLessonPreview(this, curriculum, String(activityNumber)),
            { title: entry.fields.title },
          ),
      })
    })
  }

  <template>
    <LessonsPage
      @loaderInstance={{@model.loaderInstance}}
      @title={{t "pages.product.lessons.title" product=(t "products.writing-legends")}}
    >
      <:header>
        <LessonsSearch @themeKey={{ThemeKeys.darkBlue}} />
      </:header>
      <:results>
        <div class="flex flex-wrap items-stretch justify-center gap-6">
          {{#each this.lessonPreviewViewModels as |viewModel|}}
            <LessonPreviewTile
              data-test-lesson-preview-entry={{viewModel.activityNumber}}
              class="w-72"
              @entryId={{viewModel.id}}
              @subtitle={{viewModel.title}}
              @coverUrl="{{viewModel.coverUrl}}?h=280"
              @coverClass="h-40"
              @lessonResources={{viewModel.lessonResources}}
              @openPreview={{viewModel.openPreview}}
            >
              <LessonFields>
                <dt class="text-dusty-black-200 uppercase">
                  {{t "components.tiles.lessonPreviewEntry.lessonData.textType"}}
                </dt>
                <dd class="mb-4">
                  <span>{{concat viewModel.textType}}</span><br />
                  <span class="font-bold">{{viewModel.mapTopic}}</span>
                </dd>
                <dt class="text-dusty-black-200 uppercase">
                  {{t "components.tiles.lessonPreviewEntry.lessonData.focus"}}
                </dt>
                <dd class="mb-4">
                  <span>{{viewModel.focusSummary}}</span><br />
                  <span>{{viewModel.focusActivities}}</span>
                </dd>
                <dt class="text-dusty-black-200 uppercase">
                  {{t "components.tiles.lessonPreviewEntry.lessonData.vocabulary"}}
                </dt>
                <dd>
                  <span>{{viewModel.vocabulary}}</span><br />
                </dd>
              </LessonFields>
            </LessonPreviewTile>
          {{else}}
            <NoResultsMessage data-test-empty />
          {{/each}}
        </div>
        {{#if @model.loaderInstance.value.pageCount}}
          <BasePageSelector
            class="basis-full"
            @pageCount={{@model.loaderInstance.value.pageCount}}
            @itemCount={{@model.loaderInstance.value.lessonsData.total}}
          />
        {{/if}}
      </:results>
    </LessonsPage>
  </template>
}

function lessonPreviewViewModel({
  lessonData,
  lessonPreview,
  lessonResources,
  openPreview,
}: {
  lessonData: LessonDataEntry
  lessonPreview: LessonPreviewEntry
  lessonResources: LessonResourceEntry[]
  openPreview?: () => unknown
}) {
  return {
    id: lessonData.sys.id,
    title: lessonData.fields.title,
    coverUrl: lessonPreview?.fields.cover?.fields.file?.url,
    openPreview,
    lessonResources: lessonResources
      .map(lessonResourceViewModel)
      .sort((a, b) => RESOURCE_ORDER.indexOf(a.resourceType) - RESOURCE_ORDER.indexOf(b.resourceType)),
    activityNumber: lessonData.fields.activityNumber,
    textType: lessonData.fields.textType?.join(', ') || '-',
    mapTopic: lessonData.fields.mapTopic || '',
    focusSummary: lessonData.fields.focusSummary || '-',
    focusActivities: lessonData.fields.focusActivities?.join(', ') || '',
    vocabulary: lessonData.fields.vocabulary?.join(', ') || '-',
  }
}

function lessonResourceViewModel(resource: LessonResourceEntry) {
  return {
    id: resource.sys.id,
    title: resource.fields.title,
    resourceType: resource.fields.resourceType,
    fileUrl: resource.fields.file?.fields.file?.url,
    videoUrl: resource.fields.videoUrl,
  }
}

export default RouteTemplate(WritingLegendsPreviews)
